import type { FC, ReactNode } from 'react';
import { Divider } from 'antd';
import dayjs from 'dayjs';
import { TPO_OPTIMISE_RESULT_SUMMARY, tpoPartTestId } from '../../../../config/testIds';
import type { OptimizeResultConfig, ResultSummaryFieldConfig } from '../../../../config/optimizeResultConfig';
import type { OptimiseResultSummaryData } from '../../../../types/optimiseResult';

interface Props {
    summary: OptimiseResultSummaryData;
    config: OptimizeResultConfig;
}

interface LimitConstraint {
    constraint_name: string;
    value: number;
}

function renderFieldContent(
    field: ResultSummaryFieldConfig,
    baseInputs: Record<string, unknown>,
): ReactNode {
    const value = baseInputs[field.name];
    if (value == null) return null;

    switch (field.type) {
        case 'date': {
            const formatted = dayjs.unix(Number(value)).format(field.dateFormat ?? 'MM/DD/YYYY');
            return (
                <span className="text-sm">
                    <span className="text-[#18181b]">{field.label}:</span>{' '}
                    <span className="text-[#595959]">{formatted}</span>
                </span>
            );
        }
        case 'keepTopWithContext': {
            const template = field.contextTemplate ?? 'Keep {value} events out of {totalCount}';
            const keepValue = String(value);
            const totalCount = String(baseInputs[field.totalCountField ?? 'total_promo_count'] ?? '-');
            const segments = template.split(/(\{value\}|\{totalCount\})/);
            return (
                <span className="text-sm text-[#18181b]">
                    {segments.map((seg, idx) => {
                        if (seg === '{value}')
                            return <span key={idx} className="text-[#8c8c8c]">{keepValue}</span>;
                        if (seg === '{totalCount}')
                            return <span key={idx} className="text-[#8c8c8c]">{totalCount}</span>;
                        return <span key={idx}>{seg}</span>;
                    })}
                </span>
            );
        }
        case 'lookup': {
            const displayLabel = field.options?.[String(value)] ?? String(value);
            return (
                <span className="text-sm">
                    <span className="text-[#18181b]">{field.label}:</span>{' '}
                    <span className="text-[#595959]">{displayLabel}</span>
                </span>
            );
        }
        case 'limitConstraints': {
            const constraints = value as LimitConstraint[];
            if (!Array.isArray(constraints) || constraints.length === 0) return null;
            return constraints.map((c) => {
                const label = field.constraintLabels?.[c.constraint_name] ?? c.constraint_name;
                return (
                    <span
                        key={c.constraint_name}
                        className="text-sm"
                        data-testid={tpoPartTestId(TPO_OPTIMISE_RESULT_SUMMARY, c.constraint_name)}
                    >
                        <span className="text-[#18181b]">{label}:</span>{' '}
                        <span className="text-[#595959]">{c.value}</span>
                    </span>
                );
            });
        }
        case 'text':
        default:
            return (
                <span className="text-sm">
                    <span className="text-[#18181b]">{field.label}:</span>{' '}
                    <span className="text-[#595959]">{String(value)}</span>
                </span>
            );
    }
}

const attributesByType = (
    attributes: Props['summary']['selected_attributes'],
): Map<string, string[]> => {
    const map = new Map<string, string[]>();
    for (const attr of attributes) {
        map.set(attr.attributeType, attr.attributeValues);
    }
    return map;
};

const OptimiseResultSummary: FC<Props> = ({ summary, config }) => {
    const { selected_attributes, base_inputs } = summary;
    const baseInputsRecord = base_inputs as unknown as Record<string, unknown>;
    const attrMap = attributesByType(selected_attributes);

    return (
        <div className="flex flex-col gap-4 border border-[#f0f0f0] rounded-md" data-testid={TPO_OPTIMISE_RESULT_SUMMARY}>
            <div className="px-4 pt-4">
                <p className="text-sm text-[#8c8c8c] mb-2 font-medium">{config.scopeSectionLabel}</p>
                <div className="flex flex-col gap-1.5">
                    {config.scopeDisplayFields.map((field) => {
                        const values = attrMap.get(field.attributeType);
                        const displayValue = values && values.length > 0 ? values.join(', ') : '-';
                        return (
                            <div
                                key={field.attributeType}
                                className="text-sm"
                                data-testid={tpoPartTestId(TPO_OPTIMISE_RESULT_SUMMARY, field.attributeType)}
                            >
                                <span className="text-[#18181b]">{field.label}:</span>{' '}
                                <span className="text-[#595959]">{displayValue}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <Divider className="!my-0" />

            <div className="px-4 pb-4">
                <p className="text-sm text-[#8c8c8c] mb-2 font-medium">{config.baseInputSectionLabel}</p>
                <div className="flex flex-wrap gap-x-6 gap-y-1.5">
                    {config.baseInputDisplayFields.flatMap((field): React.JSX.Element[] => {
                        const content = renderFieldContent(field, baseInputsRecord);
                        if (content == null) return [];
                        if (field.type === 'limitConstraints') return content as React.JSX.Element[];
                        return [
                            <span
                                key={field.name}
                                data-testid={tpoPartTestId(TPO_OPTIMISE_RESULT_SUMMARY, field.name)}
                            >
                                {content}
                            </span>,
                        ];
                    })}
                </div>
            </div>
        </div>
    );
};

OptimiseResultSummary.displayName = 'OptimiseResultSummary';
export default OptimiseResultSummary;
